import { createContext, useContext, type ReactNode } from 'react';

/**
 * v3.9 "Sayfaya git": genel aramanın ve Yönetim merkezi aramasının okuduğu sayfa listesi. Liste kabukta, her çizimde YETKİ
 * süzgecinden geçmiş menüden kurulur (shell/navigation.tsx `filterAreas`): yetkisi olmayan rota hiç listelenmez.
 */
export interface NavPage {
  id: string;
  label: string;
  href: string;
  icon: ReactNode;
  /** Alan adı (ör. "CRM"); Yönetim ayarında "Yönetim · Kategori" */
  where: string;
  /** Arama için ek sözcükler (Yönetim ayarlarının açıklaması) */
  keywords?: string;
}

const NavPagesContext = createContext<readonly NavPage[]>([]);

export function NavPagesProvider({
  pages,
  children,
}: {
  pages: readonly NavPage[];
  children: ReactNode;
}) {
  return <NavPagesContext.Provider value={pages}>{children}</NavPagesContext.Provider>;
}

export function useNavPages(): readonly NavPage[] {
  return useContext(NavPagesContext);
}

/** Türkçe büyük/küçük harf ve aksan duyarsız karşılaştırma biçimi (ı/i, ş/s, ğ/g, ü/u, ö/o, ç/c) */
export function normalizeTr(text: string): string {
  return text
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();
}

/** Sözcüklere böler; tire ve kesme işareti sözcüğü bölmez ("e-posta" = "eposta") */
function wordsOf(text: string): string[] {
  return normalizeTr(text)
    .replace(/[-'’.]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((w) => w !== '');
}

/**
 * Sorgudaki her sözcük sayfa adındaki, yerindeki ya da ek sözcüklerdeki bir sözcüğün BAŞINA uymalı ("tekl" → "Teklifler";
 * "istekleri" içinde eşleşmez). Sıralama: adı sorguyla başlayanlar, sonra adında eşleşenler, sonra yalnız yerinde/ek
 * sözcüklerde eşleşenler (kararlı: menü sırası korunur).
 */
export function matchPages(pages: readonly NavPage[], query: string, limit = 6): NavPage[] {
  const words = wordsOf(query);
  if (words.length === 0) return [];
  const joined = words.join(' ');
  const scored: Array<{ p: NavPage; score: number; i: number }> = [];
  pages.forEach((p, i) => {
    const label = wordsOf(p.label);
    const hay = [...label, ...wordsOf(p.where), ...wordsOf(p.keywords ?? '')];
    const hit = (pool: string[]) => words.every((w) => pool.some((h) => h.startsWith(w)));
    if (!hit(hay)) return;
    const score = label.join(' ').startsWith(joined) ? 0 : hit(label) ? 1 : 2;
    scored.push({ p, score, i });
  });
  return scored
    .sort((a, b) => a.score - b.score || a.i - b.i)
    .slice(0, limit)
    .map((x) => x.p);
}
